import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ThemeContext, type DefaultTheme } from 'styled-components';
import {
  NETWORK_ORB_ACCENT_GREEN,
  NETWORK_ORB_DEFAULT_SIZE,
  NETWORK_ORB_MAX_DENSITY_SCALE,
  NETWORK_ORB_MAX_LINK_SCALE,
  NETWORK_ORB_MAX_PARTICLE_SCALE,
  NETWORK_ORB_PALETTE_DARK,
  NETWORK_ORB_PALETTE_LIGHT,
  NETWORK_ORB_REFERENCE_SIZE,
  NETWORK_ORB_SHAPES,
  type NetworkOrbShape,
} from './NetworkOrb.constants';
import { OrbCanvas, OrbWrapper } from './NetworkOrb.styled';

export interface NetworkOrbProps {
  /**
   * Convenience prop for square orbs — sets both width and height to the same
   * value. Ignored when `width` *and* `height` are both provided. Defaults to
   * `140`, matching the design prototype.
   */
  size?: number;
  /** Explicit width in CSS pixels. When provided alongside `height`, overrides `size`. */
  width?: number;
  /** Explicit height in CSS pixels. When provided alongside `width`, overrides `size`. */
  height?: number;
  /**
   * Force a theme. When omitted (the common case), the orb reads the active
   * styled-components theme from context and switches palettes whenever the
   * surrounding `<ThemeProvider>` swaps between the Aibuild dark and light
   * themes — exactly like every other component in this package. Pass `true`
   * or `false` to lock the orb to one palette regardless of context.
   */
  isDark?: boolean;
  /**
   * Whether the orb is the click-the-green-dots interactive game (default) or
   * a passive decorative animation. Disable for non-interactive surfaces such
   * as the login splash. The visual output is otherwise identical.
   */
  interactive?: boolean;
  className?: string;
  /** Test id forwarded to the canvas element. Defaults to `network-orb-canvas`. */
  dataTestId?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  pulse: number;
  pulseSpeed: number;
  colorIdx: number;
  isGreen: boolean;
  clicked: boolean;
  targetX?: number;
  targetY?: number;
}

type GameState = 'playing' | 'animating' | 'cooldown';

interface ShapePoint {
  x: number;
  y: number;
}

/**
 * Generates the target positions used during the shape-morph animation that
 * plays after the user has cleared every green dot. The math here is a direct
 * port of the design prototype so silhouettes match pixel-for-pixel.
 *
 * Exported so the math can be unit-tested directly without having to drive the
 * full canvas animation loop.
 */
export function getShapePoints(
  shape: NetworkOrbShape,
  cx: number,
  cy: number,
  count: number,
  scale: number,
): ShapePoint[] {
  const points: ShapePoint[] = [];
  const s = scale;

  switch (shape) {
    case 'sphere': {
      for (let i = 0; i < count; i += 1) {
        const phi = Math.acos(1 - (2 * (i + 0.5)) / count);
        const theta = Math.PI * (1 + Math.sqrt(5)) * i;
        const r = 38 * s;
        points.push({
          x: cx + r * Math.sin(phi) * Math.cos(theta),
          y: cy + r * Math.cos(phi) * 0.9,
        });
      }
      break;
    }
    case 'flower': {
      for (let i = 0; i < count; i += 1) {
        const angle = (i / count) * Math.PI * 2 * 3;
        const r = (12 + 26 * Math.abs(Math.sin(angle * 2.5))) * s;
        const theta = (i / count) * Math.PI * 2;
        points.push({ x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) });
      }
      break;
    }
    case 'cube': {
      const cs = 28 * s;
      const edges: Array<[number, number, number, number]> = [
        [-1, -1, 1, -1],
        [-1, -1, -1, 1],
        [1, -1, 1, 1],
        [-1, 1, 1, 1],
        [-0.6, -0.6, 0.4, -0.6],
        [-0.6, -0.6, -0.6, 0.4],
        [1, -1, 0.4, -0.6],
        [1, 1, 0.4, 0.4],
        [-1, 1, -0.6, 0.4],
      ];
      const perEdge = Math.ceil(count / edges.length);
      for (const [x1, y1, x2, y2] of edges) {
        for (let j = 0; j < perEdge && points.length < count; j += 1) {
          const t = j / perEdge;
          points.push({
            x: cx + (x1 + (x2 - x1) * t) * cs,
            y: cy + (y1 + (y2 - y1) * t) * cs,
          });
        }
      }
      break;
    }
    case 'star': {
      for (let i = 0; i < count; i += 1) {
        const angle = (i / count) * Math.PI * 2;
        const r = (i % 3 === 0 ? 38 : 16) * s;
        points.push({
          x: cx + r * Math.cos(angle - Math.PI / 2),
          y: cy + r * Math.sin(angle - Math.PI / 2),
        });
      }
      break;
    }
    case 'smile': {
      const faceCount = Math.floor(count * 0.4);
      const eyeCount = Math.floor(count * 0.15);
      const mouthCount = count - faceCount - eyeCount * 2;
      for (let i = 0; i < faceCount; i += 1) {
        const angle = (i / faceCount) * Math.PI * 2;
        points.push({
          x: cx + 36 * s * Math.cos(angle),
          y: cy + 36 * s * Math.sin(angle),
        });
      }
      for (let i = 0; i < eyeCount; i += 1) {
        const angle = (i / eyeCount) * Math.PI * 2;
        points.push({
          x: cx - 12 * s + 5 * s * Math.cos(angle),
          y: cy - 10 * s + 5 * s * Math.sin(angle),
        });
      }
      for (let i = 0; i < eyeCount; i += 1) {
        const angle = (i / eyeCount) * Math.PI * 2;
        points.push({
          x: cx + 12 * s + 5 * s * Math.cos(angle),
          y: cy - 10 * s + 5 * s * Math.sin(angle),
        });
      }
      for (let i = 0; i < mouthCount; i += 1) {
        const angle = (i / mouthCount) * Math.PI;
        points.push({
          x: cx + 18 * s * Math.cos(angle + Math.PI),
          y: cy + 8 * s + 12 * s * Math.sin(angle + Math.PI) * -1,
        });
      }
      break;
    }
    default:
      break;
  }

  while (points.length < count) {
    points.push({
      x: cx + (Math.random() - 0.5) * 20 * s,
      y: cy + (Math.random() - 0.5) * 20 * s,
    });
  }
  return points.slice(0, count);
}

/**
 * Read dark/light from the styled-components ThemeProvider in scope.
 *
 * Looks at `theme.themeName` first (matches the Aibuild OS theme contract used
 * by both the frontend app and the Storybook preview), then `isDarkTheme`, and
 * finally defaults to dark. If no `<ThemeProvider>` is wrapping us at all the
 * theme context is `undefined` and we default to dark — matching how the orb
 * appears in its design home (dark surface).
 */
function isDarkTheme(theme: DefaultTheme | undefined): boolean {
  if (!theme) return true;
  if (theme.themeName === 'light') return false;
  if (theme.themeName === 'dark') return true;
  if (theme.isLightTheme === true) return false;
  if (theme.isDarkTheme === true) return true;
  return true;
}

/**
 * Animated particle constellation used as the Aibuild OS visual signature on
 * the home hero, login splash, and CAM nav rail. The orb is a direct port of
 * the design handoff prototype: multiple particle clusters drift inside a soft
 * boundary, edges form between near neighbours, and a rotating cast of green
 * "target" dots invite the user to click — clearing them all triggers a brief
 * shape-morph (sphere → flower → cube → star → smile) before the field resets.
 *
 * The component is a single `<canvas>` driven by `requestAnimationFrame`, with
 * no WebGL and no app-specific hooks. Light/dark is read from the standard
 * styled-components `<ThemeProvider>` (consistent with every other component
 * in this package); pass the `isDark` prop to override.
 */
const NetworkOrb: React.FC<NetworkOrbProps> = ({
  size = NETWORK_ORB_DEFAULT_SIZE,
  width,
  height,
  isDark,
  interactive = true,
  className,
  dataTestId = 'network-orb-canvas',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animRef = useRef(0);
  const timeRef = useRef(0);
  const particlesRef = useRef<Particle[]>([]);
  const greenIndicesRef = useRef<Set<number>>(new Set());
  const clickedCountRef = useRef(0);
  const gameStateRef = useRef<GameState>('playing');
  const animPhaseRef = useRef(0);
  const currentShapeRef = useRef(0);
  const shapeHoldRef = useRef(0);
  const flashRef = useRef(0);
  const roundRef = useRef(0);

  const resolvedWidth = width ?? size;
  const resolvedHeight = height ?? size;
  // Density / motion scale relative to the 140px reference design. Use the
  // smaller dimension so non-square orbs still stay nicely populated.
  const scale = Math.min(resolvedWidth, resolvedHeight) / NETWORK_ORB_REFERENCE_SIZE;

  // Read the theme through ThemeContext directly (instead of `useTheme()`)
  // so the orb still renders sensibly when no `<ThemeProvider>` is in scope —
  // `useTheme()` throws in that case, but `useContext(ThemeContext)` quietly
  // returns `undefined`, which our `isDarkTheme()` helper folds into a dark
  // default.
  const theme = useContext(ThemeContext) as DefaultTheme | undefined;
  const dark = isDark ?? isDarkTheme(theme);

  const getGreenCount = useCallback(() => {
    const total = particlesRef.current.length;
    return Math.min(3 + roundRef.current, Math.max(3, Math.floor(total * 0.4)));
  }, []);

  const resetGreenDots = useCallback(() => {
    const particles = particlesRef.current;
    for (const p of particles) {
      p.isGreen = false;
      p.clicked = false;
      p.targetX = undefined;
      p.targetY = undefined;
    }
    if (!interactive) {
      greenIndicesRef.current = new Set();
      gameStateRef.current = 'playing';
      return;
    }
    const indices = new Set<number>();
    const gc = Math.min(getGreenCount(), particles.length);
    while (indices.size < gc) {
      indices.add(Math.floor(Math.random() * particles.length));
    }
    greenIndicesRef.current = indices;
    for (const idx of indices) {
      particles[idx].isGreen = true;
      particles[idx].clicked = false;
    }
    clickedCountRef.current = 0;
    gameStateRef.current = 'playing';
    roundRef.current += 1;
  }, [getGreenCount, interactive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    canvas.width = resolvedWidth * dpr;
    canvas.height = resolvedHeight * dpr;
    canvas.style.width = `${resolvedWidth}px`;
    canvas.style.height = `${resolvedHeight}px`;
    ctx.scale(dpr, dpr);

    const cx = resolvedWidth / 2;
    const cy = resolvedHeight / 2;
    const palette = dark ? NETWORK_ORB_PALETTE_DARK : NETWORK_ORB_PALETTE_LIGHT;
    const accent = NETWORK_ORB_ACCENT_GREEN;
    const s = scale;

    const particles: Particle[] = [];

    const densityScale = Math.min(s, NETWORK_ORB_MAX_DENSITY_SCALE);
    const particleScaleCap = Math.min(s, NETWORK_ORB_MAX_PARTICLE_SCALE);
    const linkScaleCap = Math.min(s, NETWORK_ORB_MAX_LINK_SCALE);
    const leftCount = Math.round(14 * densityScale);
    const rightCount = Math.round(10 * densityScale);
    const orbitCount = Math.round(20 * densityScale);
    const scatterCount = Math.round(30 * densityScale);

    for (let i = 0; i < leftCount; i += 1) {
      particles.push({
        x: cx - 20 * s + Math.random() * 14 * s,
        y: cy - 25 * s + Math.random() * 50 * s,
        vx: (Math.random() - 0.5) * 0.12 * s,
        vy: (Math.random() - 0.5) * 0.12 * s,
        size: (1.2 + Math.random() * 1.5) * particleScaleCap,
        opacity: 0.5 + Math.random() * 0.5,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.012 + Math.random() * 0.02,
        colorIdx: Math.floor(Math.random() * palette.length),
        isGreen: false,
        clicked: false,
      });
    }

    for (let i = 0; i < rightCount; i += 1) {
      particles.push({
        x: cx + 10 * s + Math.random() * 8 * s,
        y: cy - 25 * s + Math.random() * 50 * s,
        vx: (Math.random() - 0.5) * 0.12 * s,
        vy: (Math.random() - 0.5) * 0.12 * s,
        size: (1.2 + Math.random() * 1.5) * particleScaleCap,
        opacity: 0.5 + Math.random() * 0.5,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.012 + Math.random() * 0.02,
        colorIdx: Math.floor(Math.random() * palette.length),
        isGreen: false,
        clicked: false,
      });
    }

    for (let i = 0; i < orbitCount; i += 1) {
      const angle = (i / orbitCount) * Math.PI * 2;
      const radius = (35 + Math.random() * 25) * s;
      particles.push({
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        vx: (Math.random() - 0.5) * 0.25 * s,
        vy: (Math.random() - 0.5) * 0.25 * s,
        size: (0.7 + Math.random() * 1.2) * particleScaleCap,
        opacity: 0.25 + Math.random() * 0.4,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.01 + Math.random() * 0.015,
        colorIdx: Math.floor(Math.random() * palette.length),
        isGreen: false,
        clicked: false,
      });
    }

    for (let i = 0; i < scatterCount; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 55 * s;
      particles.push({
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        vx: (Math.random() - 0.5) * 0.18 * s,
        vy: (Math.random() - 0.5) * 0.18 * s,
        size: (0.6 + Math.random() * 1.0) * particleScaleCap,
        opacity: 0.2 + Math.random() * 0.4,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.008 + Math.random() * 0.02,
        colorIdx: Math.floor(Math.random() * palette.length),
        isGreen: false,
        clicked: false,
      });
    }

    particlesRef.current = particles;
    resetGreenDots();

    const boundaryRadius = 65 * s;
    const baseConnDist = 30 * linkScaleCap;
    const animConnDist = 22 * linkScaleCap;

    const animate = () => {
      timeRef.current += 1;
      ctx.clearRect(0, 0, resolvedWidth, resolvedHeight);

      const state = gameStateRef.current;

      if (state === 'animating') {
        animPhaseRef.current = Math.min(1, animPhaseRef.current + 0.012);

        for (const p of particles) {
          if (p.targetX !== undefined && p.targetY !== undefined) {
            p.x += (p.targetX - p.x) * 0.06;
            p.y += (p.targetY - p.y) * 0.06;
          }
          p.pulse += p.pulseSpeed * 2;
        }

        if (flashRef.current > 0) {
          flashRef.current -= 0.02;
          const fg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 70 * s);
          fg.addColorStop(
            0,
            `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, ${flashRef.current * 0.3})`,
          );
          fg.addColorStop(1, `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, 0)`);
          ctx.beginPath();
          ctx.fillStyle = fg;
          ctx.arc(cx, cy, 70 * s, 0, Math.PI * 2);
          ctx.fill();
        }

        if (animPhaseRef.current >= 1) {
          shapeHoldRef.current += 1;
          if (shapeHoldRef.current > 120) {
            gameStateRef.current = 'cooldown';
            animPhaseRef.current = 0;
            shapeHoldRef.current = 0;
          }
        }
      } else if (state === 'cooldown') {
        animPhaseRef.current = Math.min(1, animPhaseRef.current + 0.015);

        for (const p of particles) {
          const dx = cx - p.x;
          const dy = cy - p.y;
          p.vx += dx * 0.0008;
          p.vy += dy * 0.0008;
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.98;
          p.vy *= 0.98;
          p.pulse += p.pulseSpeed;
        }

        if (animPhaseRef.current >= 1) {
          resetGreenDots();
          animPhaseRef.current = 0;
        }
      } else {
        for (const p of particles) {
          p.x += p.vx;
          p.y += p.vy;
          p.pulse += p.pulseSpeed;
          const dx = cx - p.x;
          const dy = cy - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > boundaryRadius) {
            p.vx += dx * 0.0003;
            p.vy += dy * 0.0003;
          }
          p.vx *= 0.999;
          p.vy *= 0.999;
        }
      }

      const connDist = state === 'animating' ? animConnDist : baseConnDist;
      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < connDist) {
            const alpha = (1 - d / connDist) * (state === 'animating' ? 0.35 : 0.18);
            const anyGreen = particles[i].isGreen || particles[j].isGreen;
            const c =
              state === 'animating' || anyGreen ? accent : palette[particles[i].colorIdx];
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})`;
            ctx.lineWidth = state === 'animating' ? 0.8 : 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        const a = p.opacity * (0.6 + 0.4 * Math.sin(p.pulse));
        const isGreenDot = p.isGreen && !p.clicked && state === 'playing';
        const isAnimating = state === 'animating';
        let c: readonly [number, number, number];
        if (isAnimating) {
          c = accent;
        } else if (isGreenDot) {
          c = accent;
        } else if (p.clicked && state === 'playing') {
          c = accent;
        } else {
          c = palette[p.colorIdx];
        }

        let drawSize: number;
        if (isGreenDot) {
          drawSize = p.size * 1.8 + Math.sin(timeRef.current * 0.08) * 0.6;
        } else if (isAnimating) {
          drawSize = p.size * 1.3;
        } else {
          drawSize = p.size;
        }

        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, drawSize * 3.5);
        grad.addColorStop(
          0,
          `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a * (isGreenDot ? 0.5 : 0.35)})`,
        );
        grad.addColorStop(1, `rgba(${c[0]}, ${c[1]}, ${c[2]}, 0)`);
        ctx.beginPath();
        ctx.fillStyle = grad;
        ctx.arc(p.x, p.y, drawSize * 3.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${isGreenDot ? 1 : a})`;
        ctx.arc(p.x, p.y, drawSize, 0, Math.PI * 2);
        ctx.fill();

        if (isGreenDot) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, ${
            0.4 + 0.3 * Math.sin(timeRef.current * 0.06)
          })`;
          ctx.lineWidth = 0.6;
          ctx.arc(p.x, p.y, drawSize + 3 * Math.min(s, 2), 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      if (state !== 'animating') {
        const cp = 0.06 + 0.03 * Math.sin(timeRef.current * 0.015);
        const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 45 * s);
        cg.addColorStop(0, `rgba(${palette[0][0]}, ${palette[0][1]}, ${palette[0][2]}, ${cp})`);
        cg.addColorStop(
          0.5,
          `rgba(${palette[1][0]}, ${palette[1][1]}, ${palette[1][2]}, ${cp * 0.5})`,
        );
        cg.addColorStop(1, `rgba(${palette[2][0]}, ${palette[2][1]}, ${palette[2][2]}, 0)`);
        ctx.beginPath();
        ctx.fillStyle = cg;
        ctx.arc(cx, cy, 45 * s, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const cp = 0.1 + 0.05 * Math.sin(timeRef.current * 0.03);
        const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 50 * s);
        cg.addColorStop(0, `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, ${cp})`);
        cg.addColorStop(1, `rgba(${accent[0]}, ${accent[1]}, ${accent[2]}, 0)`);
        ctx.beginPath();
        ctx.fillStyle = cg;
        ctx.arc(cx, cy, 50 * s, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = window.requestAnimationFrame(animate);
    };

    animate();
    return () => window.cancelAnimationFrame(animRef.current);
  }, [dark, resetGreenDots, resolvedWidth, resolvedHeight, scale]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!interactive) return;
      if (gameStateRef.current !== 'playing') return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const scaleX = resolvedWidth / rect.width;
      const scaleY = resolvedHeight / rect.height;
      const mx = (e.clientX - rect.left) * scaleX;
      const my = (e.clientY - rect.top) * scaleY;

      const particles = particlesRef.current;
      const hitRadius = 12 * Math.min(scale, NETWORK_ORB_MAX_LINK_SCALE);
      const hitRadius2 = hitRadius * hitRadius;

      for (const idx of greenIndicesRef.current) {
        const p = particles[idx];
        if (!p || p.clicked) continue;
        const dx = mx - p.x;
        const dy = my - p.y;
        if (dx * dx + dy * dy < hitRadius2) {
          p.clicked = true;
          clickedCountRef.current += 1;

          if (clickedCountRef.current >= greenIndicesRef.current.size) {
            const shape =
              NETWORK_ORB_SHAPES[currentShapeRef.current % NETWORK_ORB_SHAPES.length];
            currentShapeRef.current += 1;
            const cx = resolvedWidth / 2;
            const cy = resolvedHeight / 2;
            const targets = getShapePoints(shape, cx, cy, particles.length, scale);
            for (let i = 0; i < particles.length; i += 1) {
              particles[i].targetX = targets[i].x;
              particles[i].targetY = targets[i].y;
              particles[i].isGreen = true;
            }
            gameStateRef.current = 'animating';
            animPhaseRef.current = 0;
            shapeHoldRef.current = 0;
            flashRef.current = 1;
          }
          break;
        }
      }
    },
    [interactive, resolvedHeight, resolvedWidth, scale],
  );

  // Memoise to avoid styled-components remounting on every render with the
  // same numeric props (which would needlessly re-trigger the effect above).
  const wrapperProps = useMemo(
    () => ({
      $width: resolvedWidth,
      $height: resolvedHeight,
      $interactive: interactive,
    }),
    [resolvedWidth, resolvedHeight, interactive],
  );

  return (
    <OrbWrapper
      role="presentation"
      aria-hidden="true"
      className={className}
      {...wrapperProps}
    >
      <OrbCanvas
        ref={canvasRef}
        $isDark={dark}
        data-testid={dataTestId}
        onClick={interactive ? handleClick : undefined}
      />
    </OrbWrapper>
  );
};

export default NetworkOrb;
